import { useSetRegionMutation } from "Api/Mutations/Auth/useSetRegionMutation";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { Region } from "Infrastructure/Api/Api";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { useDispatch } from "react-redux";

/**
 * Persists the user's one-time market region before a state-changing flow runs.
 *
 * The backend blocks every POST/PUT/DELETE for a migrated user until their region claim
 * exists (RequireRegionMiddleware) — the claim is minted only by PUT /api/auth/context/region,
 * never from business payloads. A region-less user picking a region in the license form is
 * making exactly that one-time market choice, so it must be persisted first (fresh token +
 * user-info refresh) and only then may the order itself fire. A user whose region is already
 * set skips straight to the flow — their form choice stays a plain payload field.
 */
export const useEnsureUserRegion = () => {
  const dispatch = useDispatch();
  const userRegion = useLoggedInUser()?.region ?? undefined;
  const { mutate: setRegion, isPending: isSettingRegion } =
    useSetRegionMutation();

  const ensureUserRegion = (
    region: Region,
    onEnsured: () => void,
    onError: () => void,
  ) => {
    if (userRegion) {
      onEnsured();
      return;
    }

    setRegion(region, {
      onSuccess: (result) => {
        // The API client reads the token from storage per request, so the follow-up
        // call already carries the region claim; user-info refresh clears regionRequired.
        setToken(result);
        dispatch(getUserInfoAsync.request(undefined));
        onEnsured();
      },
      onError,
    });
  };

  return { ensureUserRegion, isSettingRegion };
};
